/**
 * Read an uploaded SVG file into the string a drill stores as its diagram.
 *
 * The old check required the file to START with "<svg", which rejected every
 * real-world SVG: the app's own scratch-pad export opens with an
 * `<?xml …?>` declaration, and Inkscape adds that plus a comment (and often a
 * BOM or DOCTYPE). So the file is parsed as XML and judged by its root element.
 *
 * The result is shown with innerHTML in the drill library, run mode and the
 * shared drill catalog, so it is also cleaned: scripts, embedded HTML, event
 * handlers and non-local links are removed. Editor-only baggage (Inkscape /
 * Sodipodi metadata) is dropped to keep the stored drill small.
 */

export type SvgImport = { ok: true; svg: string } | { ok: false; error: string };

/** Stored inside the drill row, so keep it well under what a row should hold. */
export const SVG_IMPORT_MAX_BYTES = 1_000_000;

const SVG_NS = 'http://www.w3.org/2000/svg';
const DROP_ELEMENTS = new Set(['script', 'foreignobject', 'iframe', 'object', 'embed', 'audio', 'video', 'metadata', 'namedview', 'handler', 'listener']);
const EDITOR_NS = ['inkscape', 'sodipodi'];
const LINK_ATTRS = new Set(['href', 'xlink:href', 'src']);

function safeLink(el: Element, value: string): boolean {
  const v = value.trim().toLowerCase();
  if (v.startsWith('#')) return true; // gradients, markers, <use> of a local symbol
  // Embedded raster images are fine on <image>; nothing else may load a URL.
  return el.localName.toLowerCase() === 'image' && /^data:image\/(png|jpe?g|gif|webp);/.test(v);
}

/** True when a style/attribute value loads anything outside the file (url(#id) is local). */
function externalUrl(v: string): boolean {
  if (/@import|expression\s*\(/i.test(v)) return true;
  for (const m of v.matchAll(/url\s*\(([^)]*)\)/gi)) {
    if (!(m[1] ?? '').trim().replace(/^['"]|['"]$/g, '').trim().startsWith('#')) return true;
  }
  return false;
}

function clean(el: Element): void {
  for (const child of Array.from(el.children)) {
    const name = child.localName.toLowerCase();
    const prefix = (child.prefix ?? '').toLowerCase();
    if (DROP_ELEMENTS.has(name) || EDITOR_NS.includes(prefix)) {
      child.remove();
      continue;
    }
    clean(child);
  }
  for (const attr of Array.from(el.attributes)) {
    const n = attr.name.toLowerCase();
    const prefix = n.includes(':') ? (n.split(':')[0] ?? '') : '';
    if (n.startsWith('on') || EDITOR_NS.includes(prefix) || EDITOR_NS.some((p) => n === 'xmlns:' + p)) el.removeAttribute(attr.name);
    else if (LINK_ATTRS.has(n) && !safeLink(el, attr.value)) el.removeAttribute(attr.name);
    else if (externalUrl(attr.value)) el.removeAttribute(attr.name);
  }
  if (el.localName.toLowerCase() === 'style' && externalUrl(el.textContent ?? '')) el.textContent = '';
}

/** "210mm", "800", "100%" → a number (units ignored; only the ratio matters). */
function len(v: string | null): number | null {
  if (!v || v.trim().endsWith('%')) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function importSvg(raw: string): SvgImport {
  const text = raw.replace(/^﻿/, '').trim();
  if (!text) return { ok: false, error: 'That file is empty' };
  if (text.length > SVG_IMPORT_MAX_BYTES) {
    return { ok: false, error: 'That SVG is too large (over 1 MB). In Inkscape, try File → Save As → Optimized SVG, or remove embedded photos.' };
  }
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const root = doc.documentElement;
  if (!root || root.getElementsByTagName('parsererror').length || doc.getElementsByTagName('parsererror').length) {
    return { ok: false, error: "That file couldn't be read as an SVG" };
  }
  if (root.localName.toLowerCase() !== 'svg') return { ok: false, error: 'That is not an SVG' };
  if (!root.namespaceURI) root.setAttribute('xmlns', SVG_NS);

  clean(root);

  // The diagram is drawn at the container's width, which needs a viewBox to
  // scale. Inkscape files normally have one; plain width/height files may not.
  if (!root.getAttribute('viewBox')) {
    const w = len(root.getAttribute('width'));
    const h = len(root.getAttribute('height'));
    if (!w || !h) return { ok: false, error: "That SVG has no size, so it can't be scaled to fit" };
    root.setAttribute('viewBox', `0 0 ${w} ${h}`);
  }

  const svg = new XMLSerializer().serializeToString(root);
  if (!/<(path|rect|circle|ellipse|line|polyline|polygon|text|image|use)\b/i.test(svg)) {
    return { ok: false, error: 'That SVG has nothing in it to show' };
  }
  return { ok: true, svg };
}

/**
 * Clean a STORED svg string so it is safe to drop into the page with
 * innerHTML. This is the render-path (read) sanitizer, and it is the real
 * security boundary.
 *
 * importSvg() above runs only in the browser when a coach uploads a file, so
 * it never sees a value written straight to the database through the REST API.
 * Supabase stores whatever JSON a client sends, and the drill `svg` fields are
 * rendered with dangerouslySetInnerHTML, so every stored svg — uploaded,
 * app-built, or injected by a malicious client — must be re-cleaned HERE,
 * every time it is rendered, using the same rules as the upload path.
 *
 * Returns '' when the value is not a parseable single <svg> (so the caller
 * shows nothing rather than raw markup). App-generated diagrams are valid SVG
 * and pass through unchanged apart from editor cruft they never contain.
 */
export function sanitizeSvgString(svg: string | null | undefined): string {
  if (!svg) return '';
  const text = String(svg).replace(/^﻿/, '').trim();
  if (!text) return '';
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const root = doc.documentElement;
  if (!root || doc.getElementsByTagName('parsererror').length || root.getElementsByTagName('parsererror').length) return '';
  if (root.localName.toLowerCase() !== 'svg') return '';
  if (!root.namespaceURI) root.setAttribute('xmlns', SVG_NS);
  clean(root);
  return new XMLSerializer().serializeToString(root);
}
